// Goblin illustrations as components. Every piece is decorative (empty alt,
// aria-hidden) and renders nothing when illustrations are switched off, so
// the layout falls back to exactly the plain interface.

import type { ReactNode } from 'react'
import {
  HERO_URL,
  momentUrl,
  roleAvatarUrl,
  useIllustrations,
  type Moment,
  type Scene,
} from '../lib/illustrations'

/** Small clay-style icons (public/goblins/icons/*.webp), for list bullets and labels. */
export type ClayIconName =
  | 'code'
  | 'pull-request'
  | 'workflow'
  | 'actions'
  | 'checks'
  | 'issues'
  | 'key'
  | 'bin'
  | 'push'
  | 'rocket'
  | 'gears'
  | 'hourglass'
  | 'wrench-broken'
  | 'coins'
  | 'checkbox'
  | 'bell'
  | 'no-entry'
  | 'branch'
  | 'signpost'
  | 'board'
  | 'stream'

/** A clay icon; nothing (or `fallback`) when illustrations are off. */
export function ClayIcon({
  name,
  size = 28,
  fallback = null,
  className = '',
}: {
  name: ClayIconName
  size?: number
  fallback?: ReactNode
  className?: string
}) {
  const [enabled] = useIllustrations()
  if (!enabled) return <>{fallback}</>
  return (
    <img
      src={`/goblins/icons/${name}.webp`}
      alt=""
      aria-hidden
      width={size}
      height={size}
      loading="lazy"
      className={`shrink-0 select-none object-contain ${className}`}
      style={{ width: size, height: size }}
    />
  )
}

/**
 * A bullet list whose bullets are clay icons; a plain disc list when
 * illustrations are off.
 */
export function ClayList({
  icon,
  items,
  className = '',
}: {
  icon: ClayIconName
  items: ReactNode[]
  className?: string
}) {
  const [enabled] = useIllustrations()
  if (!enabled) {
    return (
      <ul
        className={`list-disc space-y-1 pl-5 text-base text-ink ${className}`}
      >
        {items.map((item, index) => (
          <li key={index}>{item}</li>
        ))}
      </ul>
    )
  }
  return (
    <ul className={`space-y-1.5 text-base text-ink ${className}`}>
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2.5">
          <ClayIcon name={icon} size={22} className="mt-px" />
          <span className="min-w-0">{item}</span>
        </li>
      ))}
    </ul>
  )
}

export function MomentArt({
  moment,
  size = 96,
  className = '',
}: {
  moment: Moment
  size?: number
  className?: string
}) {
  const [enabled] = useIllustrations()
  if (!enabled) return null
  return (
    <img
      src={momentUrl(moment)}
      alt=""
      aria-hidden
      width={size}
      height={size}
      loading="lazy"
      className={`shrink-0 select-none object-contain ${className}`}
      style={{ width: size, height: size }}
    />
  )
}

/** Round role avatar; nothing when illustrations are off. */
export function RoleAvatar({
  roleName,
  size = 36,
}: {
  roleName: string | null | undefined
  size?: number
}) {
  const [enabled] = useIllustrations()
  if (!enabled) return null
  return (
    <img
      src={roleAvatarUrl(roleName)}
      alt=""
      aria-hidden
      width={size}
      height={size}
      className="shrink-0 select-none rounded-full bg-sunken object-contain"
      style={{ width: size, height: size }}
    />
  )
}

export function HeroArt({ className = '' }: { className?: string }) {
  const [enabled] = useIllustrations()
  if (!enabled) return null
  return (
    <img
      src={HERO_URL}
      alt=""
      aria-hidden
      className={`h-auto w-full select-none ${className}`}
    />
  )
}

/**
 * Class names for a faint scene behind a card or table (see `.has-scene` in
 * index.css). Pure CSS, so it switches off with `data-illustrations`.
 */
export function sceneClass(scene: Scene): string {
  return `has-scene scene-${scene}`
}
